/**
 * useBookmarkToggle Hook
 * Hook pour gérer le toggle des bookmarks avec optimistic updates
 */

"use client"

import { useCallback, useState } from "react"
import { toggleBookmark } from "../api/bookmarks-api"
import type {
    Bookmark,
    BookmarkError,
    BookmarkToggleRequest,
    UseBookmarkToggleResult,
} from "../types/bookmark.types"
import {
    addToCache,
    removeFromCache,
} from "../utils/bookmark-storage"

interface UseBookmarkToggleOptions {
  userEmail?: string
  onSuccess?: (bookmarked: boolean, bookmark?: Bookmark) => void
  onError?: (error: BookmarkError) => void
  optimistic?: boolean
}

export function useBookmarkToggle(
  options: UseBookmarkToggleOptions = {}
): UseBookmarkToggleResult {
  const {
    userEmail,
    onSuccess,
    onError,
    optimistic = true,
  } = options

  const [isToggling, setIsToggling] = useState<boolean>(false)
  const [error, setError] = useState<BookmarkError | null>(null)

  const toggle = useCallback(
    async (request: BookmarkToggleRequest) => {
      if (!userEmail) {
        const err: BookmarkError = {
          code: "NO_USER_EMAIL",
          message: "User email is required",
        }
        setError(err)
        onError?.(err)
        return
      }

      try {
        setIsToggling(true)
        setError(null)

        // Optimistic update local storage
        if (optimistic) {
          // On ne sait pas encore si c'est un add ou remove, on le saura après l'appel
          // Pour l'instant on ne fait rien, on attend la réponse
        }

        // Appel API
        const response = await toggleBookmark(request, { userEmail })

        // Mettre à jour le cache selon le résultat
        if (response.bookmarked && response.bookmark) {
          addToCache(userEmail, response.bookmark)
        } else {
          removeFromCache(userEmail, request.entityType, request.entityId)
        }

        // Callback de succès
        onSuccess?.(response.bookmarked, response.bookmark)
      } catch (err: any) {
        const bookmarkError: BookmarkError = {
          code: err.code || "TOGGLE_FAILED",
          message: err.message || "Failed to toggle bookmark",
          details: err.details,
        }
        setError(bookmarkError)
        onError?.(bookmarkError)
      } finally {
        setIsToggling(false)
      }
    },
    [userEmail, onSuccess, onError, optimistic]
  )

  return {
    toggle,
    isToggling,
    error,
  }
}
